import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';
import { useCallback, useEffect } from 'react';

import { AuthStore } from '@/features/stores/AuthStore';
import { ChatStore, InitialChatStore } from '@/features/stores/ChatStore';
import {
  clearAccessToken,
  clearRefreshToken,
  saveRefreshToken,
  setAccessToken,
} from '@/features/stores/TokensStore';

const authMethods: {
  logout: (() => void) | null;
  login: ((accessToken: string, refreshToken: string) => void) | null;
} = { logout: null, login: null };

export const setAuthMethods = (
  logout: () => void,
  login: (accessToken: string, refreshToken: string) => void,
) => {
  authMethods.logout = logout;
  authMethods.login = login;
};

export const getAuthMethods = () => authMethods;

export const useAuth = () => {
  const queryClient = useQueryClient();
  const isLogin = useStore(AuthStore, (s) => s.isLogin);
  const isAdmin = useStore(AuthStore, (s) => s.isAdmin);
  const isInitialized = useStore(AuthStore, (s) => s.isInitialized);

  const logoutFromStore = useCallback(() => {
    clearAccessToken();
    clearRefreshToken();
    AuthStore.setState((s) => ({
      ...s,
      isLogin: false,
      isAdmin: false,
    }));
    ChatStore.setState(() => InitialChatStore);
    queryClient.removeQueries({ queryKey: ['messages'] });
    queryClient.removeQueries({ queryKey: ['me'] });
    queryClient.clear();
  }, [queryClient]);

  const loginInStore = useCallback(
    (accessToken: string, refreshToken: string) => {
      setAccessToken(accessToken);
      saveRefreshToken(refreshToken);
      AuthStore.setState((s) => ({ ...s, isLogin: true }));
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    [queryClient],
  );

  useEffect(() => {
    setAuthMethods(logoutFromStore, loginInStore);
  }, [logoutFromStore, loginInStore]);

  return {
    isLogin,
    isAdmin,
    logoutFromStore,
    loginInStore,
    isInitialized,
  };
};
